/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * "Send these recipes to another family."
 *
 * Two states in one modal: compose (what you're sending, an optional note, how
 * many families may use the code) and the code itself.
 *
 * ── THE TWO THINGS THIS SCREEN HAS TO BE HONEST ABOUT ───────────────────────
 * 1. The code is shown ONCE. Only its hash is stored, so there is no "show it
 *    again" and the UI must not imply otherwise — hence the warning next to it
 *    and the deliberate friction on closing.
 * 2. Sharing hands over a COPY. The recipient can edit it, and revoking later
 *    does not reach into their library. Saying so here is better than a support
 *    conversation later.
 */

import { useState } from "react";

import { SharesApi } from "../../api/endpoints.ts";
import { Modal, Spinner } from "../../components/ui.tsx";
import { useToast } from "../../components/Toast.tsx";
import type { CreatedShare } from "../../api/types.ts";

export function ShareRecipesModal({ recipes: incoming, onClose, onShared }: {
  recipes: Array<{ id: string; title: string }>;
  onClose: () => void;
  onShared?: () => void;
}) {
  const toast = useToast();
  // Snapshot on mount. `onShared` clears the caller's selection as soon as the
  // code exists, and this modal must not blank out underneath the user when it
  // does — what was sent is now history, not live state.
  const [recipes] = useState(incoming);
  const [message, setMessage] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [allowDiscussion, setAllowDiscussion] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatedShare | null>(null);
  const [copied, setCopied] = useState(false);

  async function create() {
    setBusy(true);
    try {
      const out = await SharesApi.create(recipes.map((r) => r.id), {
        message: message.trim() || undefined,
        maxUses,
        allowDiscussion,
      });
      setCreated(out);
      onShared?.();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't create that share.", "danger");
    }
    setBusy(false);
  }

  function copy(code: string) {
    void navigator.clipboard.writeText(code);
    setCopied(true);
    toast("Code copied", "ok");
  }

  // Closing before the code has been copied loses it for good, so it asks.
  function guardedClose() {
    if (created && !copied &&
        !confirm("You haven't copied the code. It can't be shown again — close anyway?")) {
      return;
    }
    onClose();
  }

  return (
    <Modal
      title={created ? "Share code" : `Share ${recipes.length} recipe${recipes.length === 1 ? "" : "s"}`}
      onClose={guardedClose}
    >
      {!created ? (
        <div className="space-y-4">
          <div>
            <div className="label-mono mb-2">Sending</div>
            <ul className="max-h-40 space-y-1 overflow-auto text-sm">
              {recipes.map((r) => (
                <li key={r.id} className="truncate text-ink-2">· {r.title}</li>
              ))}
            </ul>
          </div>

          <label className="block">
            <span className="label-mono">Note (optional)</span>
            <input
              className="input mt-1"
              maxLength={280}
              placeholder="the two curries you liked"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>

          <label className="block">
            <span className="label-mono">How many families can use this code</span>
            <select className="input mt-1 w-auto" value={maxUses}
                    onChange={(e) => setMaxUses(Number(e.target.value))}>
              <option value={1}>Just one</option>
              <option value={3}>Up to 3</option>
              <option value={10}>Up to 10</option>
              <option value={25}>Up to 25</option>
            </select>
          </label>

          {/* OFF by default, and it stays off for the life of the code: this
              is the flag that decides whether a conversation can exist at all,
              so it is a decision made once, here, with the consequence spelled
              out rather than in a tooltip. */}
          <label className="flex items-start gap-2 rounded-lg border border-rule-soft bg-paper-2 p-3">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={allowDiscussion}
              onChange={(e) => setAllowDiscussion(e.target.checked)}
            />
            <span className="text-sm">
              <span className="font-medium text-ink">Open a shared discussion</span>
              <span className="mt-0.5 block text-xs text-ink-3">
                Families who accept can opt in to a comment thread on this recipe,
                visible to everyone who has it — including families this code is
                passed on to. Your private notes and meal plans are never shared.
                You can close the discussion later.
              </span>
            </span>
          </label>

          {/* Said before the code exists, while it's still a decision. */}
          <p className="rounded-lg border border-rule-soft bg-paper-2 p-3 text-xs text-ink-3">
            They get their own <strong className="text-ink-2">copy</strong> to edit as they like.
            Your version stays yours, and later edits won't reach them. Revoking the code stops
            new families using it — it doesn't take back copies already made.
          </p>

          <div className="flex justify-end gap-2">
            <button className="btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary" disabled={busy} onClick={() => void create()}>
              {busy ? <Spinner /> : "Create share code"}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-rule-soft bg-paper p-4 text-center">
            <div className="font-mono text-2xl tracking-[0.25em] text-ink">{created.code}</div>
            <button className="mt-2 text-xs text-accent hover:underline"
                    onClick={() => copy(created.code)}>
              {copied ? "Copied ✓" : "Copy code"}
            </button>
          </div>

          <p className="rounded-lg border border-warn/40 bg-warn/10 p-3 text-xs text-ink-2">
            <strong>Copy this now.</strong> Only a hash of it is stored, so it can't be shown
            again. Expires in 14 days
            {created.recipeCount === 1 ? "" : ` · ${created.recipeCount} recipes`}.
          </p>

          {created.skipped.length > 0 && (
            <p className="text-xs text-ink-3">
              {created.skipped.length} recipe{created.skipped.length === 1 ? " was" : "s were"}{" "}
              left out — archived recipes can't be shared.
            </p>
          )}

          <p className="text-xs text-ink-3">
            The other family enters this under Family → Add shared recipes.
          </p>

          <div className="flex justify-end">
            <button className="btn-primary" onClick={guardedClose}>Done</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
